import React, { useState, useEffect, useRef, useMemo } from "react";
import { formatNumber } from "utils/formators";
import { ChevronDown } from "lucide-react";
import { capitalize } from "lodash";
import { ResidencesIcon } from "components/icons/components/ResidencesIcon";
import { StarIcon } from "components/icons/components/Star";
import { calculateRouteFitScorePercentage } from "components/audience-map/utils";
import cn from "classnames";
import { Accordion, AccordionTrigger, AccordionDetails } from "../../components/AddressesBlock/AddressBlock";
import { getTooltipPosition } from "./utils";

// Tooltip "beak" carets. Inline SVGs (using the original MUI ArrowDrop/Arrow
// triangle paths) filled with `currentColor` so the pointer matches the card
// surface and inverts in dark mode — the wrapper sets `text-card`.
const beakBase = "absolute h-6 w-6 fill-current text-card";
const TopBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "top-[-14px]")} aria-hidden>
    <path d="M7 14l5-5 5 5z" />
  </svg>
);
const BottomBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "bottom-[-14px]")} aria-hidden>
    <path d="M7 10l5 5 5-5z" />
  </svg>
);
const RightBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "right-[-14px]")} aria-hidden>
    <path d="M10 17l5-5-5-5v10z" />
  </svg>
);
const LeftBeak = () => (
  <svg viewBox="0 0 24 24" className={cn(beakBase, "left-[-14px]")} aria-hidden>
    <path d="M14 7l-5 5 5 5V7z" />
  </svg>
);

const statisticsBlockContainer =
  "cb-v2-root absolute z-[9999] flex items-center justify-around rounded-[8px] border border-solid border-transparent bg-card px-[18px] py-2 shadow-[0px_2px_6px_0px_#27375414] pointer-events-none dark:border-border";
const statisticsSection = "flex flex-col items-start justify-between";
const statisticsAmount = "flex items-center text-sm font-bold text-[#222336] dark:text-foreground [&_svg]:mr-[3px]";
const statisticsLabel = "text-xs text-[#0C172C] dark:text-foreground";
const verticalSeparator = "ml-3 mr-4 h-[25px] w-px bg-[#E2E2FC] dark:bg-border";

export const RouteStatisticsV2 = ({ route, selectedRoutes, costPerFlyer, mousePositionCoords, selectedTargets }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mobRoute, setMobRoute] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipArrowPosition, setTooltipArrowPosition] = useState("bottom");
  const containerRef = useRef(null);
  const currentContainerRefBounds = useMemo(
    () => containerRef.current?.getBoundingClientRect(),
    [containerRef.current]
  );

  useEffect(() => {
    if (route) {
      setMobRoute(route);
    }
  }, [route]);

  useEffect(() => {
    if (containerRef.current && mousePositionCoords) {
      const { arrowPosition, newTooltipPosition } = getTooltipPosition({ containerRef, mousePositionCoords });
      setTooltipArrowPosition(arrowPosition);
      setTooltipPosition(newTooltipPosition);
    }
  }, [mousePositionCoords, currentContainerRefBounds]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const mailPieces = route ? route.HOME_COUNT + route.APT_COUNT : 0;
  const totalRouteCost = route && costPerFlyer ? mailPieces * costPerFlyer : 0;
  const fitScore = calculateRouteFitScorePercentage(route, selectedTargets);

  const processedCityName = route?.ZIP_NAME
    ? route.ZIP_NAME.split(" ")
        .map((part) => capitalize(part))
        .join(" ")
    : "";

  return (
    <>
      {/* desk */}
      {route && !!mousePositionCoords ? (
        <div
          ref={containerRef}
          className={cn(statisticsBlockContainer, "mob-hidden")}
          style={{ top: `${tooltipPosition.y}px`, left: `${tooltipPosition.x}px` }}
        >
          {tooltipArrowPosition === "bottom" && <TopBeak />}
          {tooltipArrowPosition === "top" && <BottomBeak />}
          {tooltipArrowPosition === "right" && <RightBeak />}
          {tooltipArrowPosition === "left" && <LeftBeak />}
          <div className={statisticsSection}>
            <span className={statisticsLabel}>Zip code - Route</span>
            <span className={statisticsAmount}>{`${route.ZIP}-${route.CROUTE}`}</span>
          </div>
          <div className={verticalSeparator} />
          {!!selectedTargets?.length && (
            <>
              <div className={statisticsSection}>
                <span className={statisticsLabel}>Fit score</span>
                <span className={statisticsAmount}>
                  <StarIcon stroke="currentColor" />
                  {fitScore}%
                </span>
              </div>
              <div className={verticalSeparator} />
            </>
          )}
          <div className={statisticsSection}>
            <span className={statisticsLabel}>Residences</span>
            <span className={statisticsAmount}>
              <ResidencesIcon stroke="currentColor" />
              {route.HOME_COUNT + route.APT_COUNT}
            </span>
          </div>
          <div className={verticalSeparator} />
          <div className={statisticsSection}>
            <span className={statisticsLabel}>Costs</span>
            <span className={statisticsAmount}>
              <span>$</span> <span className="ml-[3px]">{totalRouteCost.toFixed(2)}</span>
            </span>
          </div>
        </div>
      ) : null}

      {/* mob */}
      {selectedRoutes && selectedRoutes.length > 0 && mobRoute && (
        <div className="desk-hidden">
          <Accordion>
            <div
              style={{ position: "relative" }}
              className={cn("mob-statistics-block-container mob-address-container text-foreground dark:!bg-card")}
            >
              <AccordionTrigger onClick={handleToggle}>
                <div className="mob-routestatistics-padding text-foreground">
                  <p className="mob-route-title">Route statistics</p>
                  <ChevronDown className={cn("h-6 w-6", { open: isOpen })} />
                </div>
              </AccordionTrigger>

              <AccordionDetails isOpen={isOpen}>
                {mobRoute ? (
                  // Mobile stats: the container's global forced-white bg is overridden
                  // to bg-card in dark (dark:!bg-card on the wrapper above), and text
                  // uses the foreground token so it's legible in both themes.
                  <div className="cb-v2-root text-foreground" style={{ padding: "5px 10px" }}>
                    <div className="mb-[5px] flex items-center justify-start text-lg font-bold">
                      <span>
                        {mobRoute.ZIP} - {mobRoute.CROUTE} {mobRoute.ZIP_NAME}
                      </span>
                    </div>
                    <div className="mb-[5px] flex items-center justify-between">
                      <span>Addresses</span>
                      <span>{formatNumber(mobRoute.HOME_COUNT + mobRoute.APT_COUNT) || "-"}</span>
                    </div>
                    <div className="mb-[5px] flex items-center justify-between">
                      <span>Business</span> <span>{formatNumber(mobRoute.BUSINESS_C) || "-"}</span>
                    </div>
                    <div className="mb-[5px] h-px w-full bg-[#AEAFC7] dark:bg-border" />
                    <div className="mb-[5px] flex items-center justify-between">
                      <span>Total addresses (exc. businesses)</span>
                      <span>{formatNumber(mobRoute.TOTAL_COUN - mobRoute.BUSINESS_C) || "-"}</span>
                    </div>
                  </div>
                ) : null}
              </AccordionDetails>
            </div>
          </Accordion>
        </div>
      )}
    </>
  );
};
